import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/600.css";
import "@fontsource/montserrat/700.css";

import { PaletteMode, ThemeProvider as MuiThemeProvider } from "@mui/material";
import {
  getAllowedMessageOrigins,
  getRuntimeTypographyFont,
} from "src/infrastructure/auth/core/auth-core.runtime";
import { buildAppTheme } from "./buildAppTheme";
import { createThemeChannel, type ThemeChannel } from "./themeChannel";

interface AppThemeSwitchContextValue {
  mode: PaletteMode;
  setPaletteMode(nextMode: PaletteMode): void;
}

const storageKey = "palette-mode";
const getInitialPaletteMode = (): PaletteMode => {
  try {
    const value = localStorage.getItem(storageKey);
    if (value === "light" || value === "dark") {
      return value;
    }
  } catch {
    /* storage unavailable — fall through to default */
  }
  return "light";
};

const AppThemeSwitchContext = React.createContext<AppThemeSwitchContextValue>({
  mode: "light",
  setPaletteMode: () => {},
});

/**
 * Theme Provider matching the Insight project's visual design.
 *
 * State + wiring only: the theme object comes from `buildAppTheme` (pure) and
 * the cross-frame sync from a `ThemeChannel`. A local toggle both updates
 * state and broadcasts; an inbound change from the channel only updates state.
 */
export function ThemeProvider(props: PropsWithChildren) {
  const [mode, setMode] = React.useState<PaletteMode>(getInitialPaletteMode);

  const applyMode = useCallback((nextMode: PaletteMode) => {
    setMode(nextMode);
    try {
      localStorage.setItem(storageKey, nextMode);
    } catch {
      /* storage unavailable — keep the in-memory mode */
    }
  }, []);

  // Keep the channel's mode getter current without re-creating the channel.
  const modeRef = useRef(mode);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  const channelRef = useRef<ThemeChannel | null>(null);
  useEffect(() => {
    const channel = createThemeChannel({
      allowedOrigins: getAllowedMessageOrigins(),
      getMode: () => modeRef.current,
      onThemeChange: applyMode,
    });
    channelRef.current = channel;
    channel.requestFromParent();
    return () => {
      channel.dispose();
      channelRef.current = null;
    };
  }, [applyMode]);

  const setPaletteMode = useCallback(
    (nextMode: PaletteMode) => {
      applyMode(nextMode);
      channelRef.current?.broadcast(nextMode);
    },
    [applyMode]
  );

  const appThemeSwitchContext = useMemo(() => ({ mode, setPaletteMode }), [mode, setPaletteMode]);

  const appTheme = useMemo(
    () => buildAppTheme(mode, { typographyFont: getRuntimeTypographyFont() }),
    [mode]
  );

  return (
    <AppThemeSwitchContext.Provider value={appThemeSwitchContext}>
      <MuiThemeProvider theme={appTheme}>{props.children}</MuiThemeProvider>
    </AppThemeSwitchContext.Provider>
  );
}

/**
 * Hook to access theme switching functionality.
 *
 * @example
 * const { mode, setPaletteMode } = useAppThemeSwitchContext();
 * setPaletteMode(mode === 'light' ? 'dark' : 'light');
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAppThemeSwitchContext = () => useContext(AppThemeSwitchContext);
